/**
 * Workspace Service
 *
 * Orchestrates API calls for Workspace mode and transforms responses
 * into the format expected by WorkspaceView.
 *
 * This is the bridge between:
 * - ModernizeAPIAdapter (raw API calls)
 * - WorkspaceView (graph visualization)
 *
 * NO HARDCODING - Works with any COBOL application upload.
 */

import { createModernizeAPIAdapter } from '../adapters/api/ModernizeAPIAdapter'
import { pollJobUntilComplete } from '../utils/jobPoller.js'
import { getAPIBaseUrl } from '../environment.js'

// Create API adapter instance
const api = createModernizeAPIAdapter()

// ============================================================================
// CONSTANTS
// ============================================================================

const POLL_INTERVAL = 2000 // 2 seconds
const MAX_POLL_ATTEMPTS = 150 // 5 minutes max

// ============================================================================
// GRAPH TRANSFORMATION UTILITIES
// ============================================================================

/**
 * Normalize manifest response to standard { files: [...] } format.
 *
 * Handles multiple manifest structures:
 * - file_catalog.json: { files: [...] } (ingest engine)
 * - manifest.json: { produced_artifacts: [...] } (worker framework)
 * - classified_catalog.json: { files: [...] } (ingest engine)
 *
 * @param {Object} rawManifest - Raw manifest response from API
 * @returns {Object} Normalized { files: [...] }
 */
function normalizeIngestManifest(rawManifest) {
  if (!rawManifest) {
    console.warn('[WorkspaceService] normalizeIngestManifest: null/undefined manifest')
    return { files: [] }
  }

  // Log raw structure for debugging
  console.log('[WorkspaceService] Raw manifest keys:', Object.keys(rawManifest))

  // Log sample of raw manifest for deep debugging
  if (Object.keys(rawManifest).length > 0) {
    const manifestSample = JSON.stringify(rawManifest).substring(0, 1000)
    console.log('[WorkspaceService] Raw manifest sample (first 1000 chars):', manifestSample)
  }

  // Try multiple structure patterns
  let files = null

  // Pattern 1: file_catalog.json or classified_catalog.json (ingest engine)
  // { files: [{ path, size, content_type }] }
  if (rawManifest.files && Array.isArray(rawManifest.files)) {
    console.log('[WorkspaceService] Found files array (file_catalog structure)')
    files = rawManifest.files
  }
  // Pattern 2: Worker framework manifest.json
  // { produced_artifacts: [{ name, uri, size, artifact_type }] }
  else if (rawManifest.produced_artifacts && Array.isArray(rawManifest.produced_artifacts)) {
    console.log('[WorkspaceService] Found produced_artifacts (worker manifest structure)')
    // Convert produced_artifacts to files format
    files = rawManifest.produced_artifacts
      .filter(artifact => artifact.name && artifact.name !== 'manifest.json')
      .map(artifact => ({
        path: artifact.name,
        size: artifact.size || 0,
        content_type: artifact.artifact_type || 'application/octet-stream'
      }))
  }
  // Pattern 3: Nested file_catalog
  // { file_catalog: { files: [...] } }
  else if (rawManifest.file_catalog?.files && Array.isArray(rawManifest.file_catalog.files)) {
    console.log('[WorkspaceService] Found nested file_catalog.files')
    files = rawManifest.file_catalog.files
  }
  // Pattern 4: items array (alternative naming)
  else if (rawManifest.items && Array.isArray(rawManifest.items)) {
    console.log('[WorkspaceService] Found items array')
    files = rawManifest.items
  }
  // Pattern 5: data.files nested structure
  else if (rawManifest.data?.files && Array.isArray(rawManifest.data.files)) {
    console.log('[WorkspaceService] Found data.files')
    files = rawManifest.data.files
  }

  if (!files || files.length === 0) {
    console.warn('[WorkspaceService] No files found in manifest after trying all patterns. Keys:', Object.keys(rawManifest))
    console.warn('[WorkspaceService] Raw manifest sample:', JSON.stringify(rawManifest).substring(0, 500))
    return { files: [] }
  }

  console.log(`[WorkspaceService] Normalized manifest: ${files.length} files found`)
  return { files }
}

/**
 * Transform Ingest Manifest into COBOL source components (Workspace Source view)
 *
 * Builds component inventory from uploaded COBOL source files ONLY.
 * Does NOT include generated artifacts (Java, pom.xml, etc.)
 *
 * @param {Object} ingestManifest - Ingest manifest with files array
 * @returns {Object} { components: [...] }
 */
export function transformIngestManifestToComponents(ingestManifest) {
  const components = []

  // Normalize manifest structure first (handles multiple formats)
  const normalized = normalizeIngestManifest(ingestManifest)

  if (!normalized.files || normalized.files.length === 0) {
    console.warn('[WorkspaceService] No files in normalized manifest')
    return { components }
  }

  console.log(`[WorkspaceService] Processing ${normalized.files.length} normalized files`)
  normalized.files.forEach((file, idx) => {
    const filePath = file.path || file.name
    if (!filePath) return

    // Determine component type from file extension and path
    let componentType = 'file'
    let language = 'unknown'
    const fileName = filePath.split('/').pop()
    const fileExt = fileName.toLowerCase().split('.').pop()

    // Classify by extension
    if (['cbl', 'cob', 'cobol'].includes(fileExt)) {
      componentType = 'program'
      language = 'cobol'
    } else if (['cpy', 'copy'].includes(fileExt)) {
      componentType = 'copybook'
      language = 'cobol-copybook'
    } else if (fileExt === 'jcl') {
      componentType = 'jcl'
      language = 'jcl'
    } else if (['dat', 'data', 'txt', 'csv'].includes(fileExt)) {
      componentType = 'data'
      language = 'data'
    } else {
      // Skip non-COBOL files (Java, XML, etc.)
      return
    }

    // ✅ CANONICAL ID: Use file path as unique identifier
    // Format: "file:relative/path/to/file.ext"
    // This ensures file-based components have unique IDs that won't conflict with fallback nodes
    const canonicalId = `file:${filePath}`

    components.push({
      id: canonicalId,  // ✅ Changed to canonical file-based ID
      type: componentType,
      name: fileName,
      language: language,
      path: filePath,
      metadata: {
        size: file.size || 0,
        source: 'ingest',  // Mark as original source file
        basename: fileName.replace(new RegExp(`\\.${fileExt}$`, 'i'), ''),  // Store basename for alias mapping
      },
      tags: [],
    })
  })

  console.log('[WorkspaceService] Built components from ingest manifest:', {
    total: components.length,
    programs: components.filter(c => c.type === 'program').length,
    copybooks: components.filter(c => c.type === 'copybook').length,
    jcl: components.filter(c => c.type === 'jcl').length,
    data: components.filter(c => c.type === 'data').length,
  })

  return { components }
}

/**
 * Extract COBOL relationships from Code Analysis cross_reference.json
 *
 * cross_reference structure:
 * {
 *   copybook_usage: { "COPYBOOK.cpy": ["PROGRAM1.cbl", "PROGRAM2.cbl"], ... },
 *   call_graph: { "PROGRAM1.cbl": ["PROGRAM2.cbl", "PROGRAM3.cbl"], ... },
 *   file_usage: { "PROGRAM.cbl": [...], ... },
 *   data_names: { ... }
 * }
 *
 * @param {Object} crossReference - cross_reference.json from Code Analysis
 * @param {Array} components - Component inventory to build alias map
 * @returns {Object} { relationships: [...] }
 */
export function extractCobolRelationships(crossReference, components = []) {
  const relationships = []

  if (!crossReference) {
    return { relationships }
  }

  // Build alias map: basename → canonical component ID
  // Example: "CUSTUPD" → "file:src/CUSTUPD.cbl"
  const aliasMap = {}
  components.forEach(comp => {
    // Extract basename from metadata (if available) or from comp.name
    const baseName = comp.metadata?.basename || comp.name?.replace(/\.(cbl|cpy|jcl|dat|cob|copy|data|txt|csv|CBL|CPY|JCL|DAT|COB|COPY|DATA|TXT|CSV)$/i, '')
    if (baseName) {
      // Map all case variations to the same canonical ID
      aliasMap[baseName.toUpperCase()] = comp.id
      aliasMap[baseName.toLowerCase()] = comp.id
      aliasMap[baseName] = comp.id  // Preserve original case
    }
  })

  console.log(' [WorkspaceEdges] Built alias map:', {
    totalAliases: Object.keys(aliasMap).length / 3, // Each component has 3 aliases (upper, lower, original)
    sampleAliases: Object.entries(aliasMap).slice(0, 10).map(([k, v]) => `${k} → ${v}`),
  })

  // Extract INCLUDES relationships (copybook_usage)
  // Format: { "COPYBOOK.cpy": ["PROGRAM1.cbl", "PROGRAM2.cbl"] }
  // Meaning: COPYBOOK.cpy is included by PROGRAM1 and PROGRAM2
  const copybookUsage = crossReference.copybook_usage || {}
  Object.entries(copybookUsage).forEach(([copybook, programs]) => {
    const copybookName = copybook.replace(/\.(cpy|CPY|copy|COPY)$/i, '')

    programs.forEach(program => {
      const programName = program.replace(/\.(cbl|CBL|cob|COB)$/i, '')

      // Resolve to canonical component IDs via alias map
      const sourceId = aliasMap[programName] || `cmp_program_${programName}`
      const targetId = aliasMap[copybookName] || `cmp_copybook_${copybookName}`

      relationships.push({
        id: `rel_includes_${programName}_${copybookName}`,
        from_component_id: sourceId,  // ✅ Changed from 'source' to match WorkspaceView
        to_component_id: targetId,    // ✅ Changed from 'target' to match WorkspaceView
        relationship_type: 'INCLUDES', // ✅ Changed from 'type' to match WorkspaceView
        label: 'INCLUDES',
        metadata: {
          source_file: program,
          copybook_file: copybook,
          resolved_source: sourceId,
          resolved_target: targetId,
        }
      })
    })
  })

  // Extract CALLS relationships (call_graph)
  // Format: { "PROGRAM1.cbl": ["PROGRAM2.cbl", "PROGRAM3.cbl"] }
  // Meaning: PROGRAM1 calls PROGRAM2 and PROGRAM3
  const callGraph = crossReference.call_graph || {}
  Object.entries(callGraph).forEach(([caller, callees]) => {
    const callerName = caller.replace(/\.(cbl|CBL|cob|COB)$/i, '')

    callees.forEach(callee => {
      const calleeName = callee.replace(/\.(cbl|CBL|cob|COB)$/i, '')

      // Resolve to canonical component IDs via alias map
      const sourceId = aliasMap[callerName] || `cmp_program_${callerName}`
      const targetId = aliasMap[calleeName] || `cmp_program_${calleeName}`

      relationships.push({
        id: `rel_calls_${callerName}_${calleeName}`,
        from_component_id: sourceId,  // ✅ Changed from 'source' to match WorkspaceView
        to_component_id: targetId,    // ✅ Changed from 'target' to match WorkspaceView
        relationship_type: 'CALLS',   // ✅ Changed from 'type' to match WorkspaceView
        label: 'CALLS',
        metadata: {
          caller_file: caller,
          callee_file: callee,
          resolved_source: sourceId,
          resolved_target: targetId,
        }
      })
    })
  })

  console.log(' [WorkspaceEdges] Extracted COBOL relationships from cross_reference:', {
    includes: relationships.filter(r => r.relationship_type === 'INCLUDES').length,
    calls: relationships.filter(r => r.relationship_type === 'CALLS').length,
    total: relationships.length,
  })

  // Log first 5 relationships for verification
  console.log(' [WorkspaceEdges] Sample relationships (first 5):', relationships.slice(0, 5))

  return { relationships }
}

/**
 * Transform Code Analysis response into graph components
 *
 * NOTE: The API response summary only contains COUNTS (e.g., paragraphs: 393), not arrays.
 * The actual data is in S3 artifact files. This function works with:
 * - Direct API response (summary counts only - creates minimal graph)
 * - Enriched data with artifacts (full graph with all components)
 *
 * @param {Object} codeAnalysisResult - Response from /codeanalysis endpoint
 * @param {Object} artifactData - Optional enriched data from S3 artifacts
 * @returns {Object} { components: [...], relationships: [...] }
 */
export function transformCodeAnalysisToGraph(codeAnalysisResult, artifactData = null) {
  const components = []
  const relationships = []

  // If we have artifact data (fetched separately), use that for full detail
  if (artifactData) {
    return transformArtifactDataToGraph(artifactData, codeAnalysisResult)
  }

  // Otherwise, create a minimal graph from the summary response
  if (!codeAnalysisResult) {
    return { components, relationships }
  }

  const summary = codeAnalysisResult.summary || {}
  const mainProgram = codeAnalysisResult.main_program || codeAnalysisResult.base_name

  // Create a component for the main program from response
  if (mainProgram) {
    const programName = mainProgram.replace(/\.(cbl|CBL|cob|COB)$/i, '')
    components.push({
      id: `cmp_program_${programName}`,
      type: 'program',
      name: programName,
      language: 'cobol',
      path: mainProgram,
      metadata: {
        lines: summary.source_lines || 0,
        paragraphs: summary.paragraphs || 0,
        data_items: summary.data_items || 0,
        java_lines: summary.java_lines || 0,
      },
      tags: summary.zero_loss ? ['zero-loss'] : [],
    })
  }

  // Add info about files analyzed (from summary count)
  if (summary.files_analyzed > 1 && mainProgram) {
    // Indicate there are more files
    components[0].metadata.total_files = summary.files_analyzed
  }

  return { components, relationships }
}

/**
 * Transform artifact data (from S3 files) into full graph components
 * @param {Object} artifactData - Data fetched from S3 artifacts
 * @param {Object} apiResponse - Original API response for job info
 * @returns {Object} { components: [...], relationships: [...] }
 */
function transformArtifactDataToGraph(artifactData, apiResponse) {
  const components = []
  const relationships = []

  // Process static analysis data (has programs array)
  const staticAnalysis = artifactData.static_analysis || {}
  const programs = staticAnalysis.programs || []

  programs.forEach((prog, idx) => {
    const progName = prog.program || prog.name
    if (!progName) return

    components.push({
      id: `cmp_program_${progName}`,
      type: 'program',
      name: progName,
      language: 'cobol',
      path: prog.file_path || `cobol/${progName}.cbl`,
      metadata: {
        lines: prog.lines_of_code || prog.lines || 0,
      },
      tags: [],
    })

    // Extract CALL relationships
    const calls = prog.calls || []
    calls.forEach((call, callIdx) => {
      const target = call.target || call.callee
      if (target) {
        relationships.push({
          id: `rel_call_${progName}_${callIdx}`,
          from_component_id: `cmp_program_${progName}`,
          to_component_id: `cmp_program_${target}`,
          relationship_type: 'CALLS',
        })
      }
    })

    // Extract COPY relationships
    const copies = prog.copies || []
    copies.forEach((copy, copyIdx) => {
      const copybook = copy.copybook || copy.name
      if (copybook) {
        // Ensure copybook component exists
        const copybookId = `cmp_copybook_${copybook.replace(/\.(cpy|CPY)$/i, '')}`
        if (!components.find(c => c.id === copybookId)) {
          components.push({
            id: copybookId,
            type: 'copybook',
            name: copybook,
            language: 'cobol-copybook',
            path: `copybooks/${copybook}`,
            metadata: {},
            tags: [],
          })
        }
        relationships.push({
          id: `rel_copy_${progName}_${copyIdx}`,
          from_component_id: `cmp_program_${progName}`,
          to_component_id: copybookId,
          relationship_type: 'INCLUDES',
        })
      }
    })
  })

  return { components, relationships }
}

/**
 * Transform Dependency Mapper response into graph data
 *
 * NOTE: The API response summary only contains nested summaries (counts/stats), not the full
 * nodes/edges arrays. The actual data is in S3 artifact files. This function works with:
 * - Direct API response (creates graph from what's available)
 * - Enriched data with artifacts (full graph with all nodes/edges)
 *
 * @param {Object} depMapperResult - Response from /dependencymapper endpoint
 * @param {Object} artifactData - Optional enriched data from S3 artifacts
 * @returns {Object} { components: [...], relationships: [...], serviceBoundaries: [...] }
 */
export function transformDependencyMapperToGraph(depMapperResult, artifactData = null) {
  const components = []
  const relationships = []
  const serviceBoundaries = []

  // If we have artifact data, use the full graph
  if (artifactData) {
    return transformDepMapperArtifactsToGraph(artifactData)
  }

  if (!depMapperResult?.summary) {
    return { components, relationships, serviceBoundaries }
  }

  // The summary.graph contains summary stats, not actual nodes/edges
  // summary.graph = { total_nodes: X, total_edges: Y, program_count: Z, ... }
  // We can't build detailed graph from just counts

  return { components, relationships, serviceBoundaries }
}

/**
 * Transform Dependency Mapper artifact data into full graph
 * @param {Object} artifactData - Data fetched from S3 artifacts
 * @returns {Object} { components: [...], relationships: [...], serviceBoundaries: [...] }
 */
function transformDepMapperArtifactsToGraph(artifactData) {
  const components = []
  const relationships = []
  const serviceBoundaries = []

  // Process dependency_graph.json (has nodes and edges arrays)
  const depGraph = artifactData.dependency_graph || {}
  const nodes = depGraph.nodes || []
  const edges = depGraph.edges || []

  // Build components from nodes
  nodes.forEach((node) => {
    const nodeId = node.id || node.name
    if (!nodeId) return

    // Determine component type from node.type or infer from file extension
    let componentType = 'program'
    let language = 'cobol'

    const nodeType = node.type || ''
    const filePath = node.file_path || nodeId
    const fileExt = filePath.toLowerCase().split('.').pop()

    // Map backend types to frontend types
    if (nodeType === 'copybook' || fileExt === 'cpy') {
      componentType = 'copybook'
      language = 'cobol-copybook'
    } else if (fileExt === 'jcl') {
      componentType = 'jcl'
      language = 'jcl'
    } else if (nodeType === 'queue') {
      componentType = 'queue'
      language = 'mq'
    } else if (nodeType === 'screen') {
      componentType = 'screen'
      language = 'cics'
    } else if (nodeType === 'table') {
      componentType = 'table'
      language = 'db2'
    } else if (nodeType === 'vsam') {
      componentType = 'vsam'
      language = 'vsam'
    } else if (nodeType === 'ims_segment') {
      componentType = 'ims'
      language = 'ims'
    } else if (nodeType === 'sequential_file') {
      componentType = 'sequential'
      language = 'sequential'
    } else if (nodeType === 'data_file' || ['dat', 'data', 'txt', 'csv'].includes(fileExt)) {
      componentType = 'data'
      language = 'data'
    } else if (['cbl', 'cob', 'cobol'].includes(fileExt) || nodeType === 'program') {
      componentType = 'program'
      language = 'cobol'
    }

    components.push({
      id: `cmp_${componentType}_${nodeId}`,
      type: componentType,
      name: nodeId,
      language: language,
      path: node.file_path || null,
      metadata: {
        fan_in: node.fan_in || 0,
        fan_out: node.fan_out || 0,
        lines: node.lines_of_code || 0,
        complexity: node.complexity_score || 0,
      },
      tags: [],
    })
  })

  // Build relationships from edges
  edges.forEach((edge, idx) => {
    const fromId = edge.from || edge.source
    const toId = edge.to || edge.target
    const edgeType = edge.type || 'CALLS'

    if (!fromId || !toId) return

    // Find actual component types from the components we just created
    const fromComponent = components.find(c => c.name === fromId)
    const toComponent = components.find(c => c.name === toId)

    const fromPrefix = fromComponent?.type || 'program'
    const toPrefix = toComponent?.type || (edgeType === 'COPY' ? 'copybook' : 'program')

    relationships.push({
      id: `rel_dm_${idx}`,
      from_component_id: `cmp_${fromPrefix}_${fromId}`,
      to_component_id: `cmp_${toPrefix}_${toId}`,
      relationship_type: edgeType,
    })
  })

  // Process microservice_boundaries.json
  const msBoundaries = artifactData.microservice_boundaries || {}
  const boundaries = msBoundaries.boundaries || msBoundaries.services || []

  boundaries.forEach((boundary, idx) => {
    const boundaryName = boundary.name || boundary.service_name || `Service${idx + 1}`
    const boundaryPrograms = boundary.programs || boundary.components || boundary.members || []

    serviceBoundaries.push({
      id: `svc_${idx}`,
      serviceName: boundaryName,
      confidence: boundary.confidence || 'MEDIUM',
      components: boundaryPrograms.map(prog => ({
        id: `cmp_program_${typeof prog === 'string' ? prog : prog.name}`,
        name: typeof prog === 'string' ? prog : prog.name,
        type: 'program',
        reason: boundary.reason || 'Grouped by dependency analysis',
      })),
      reasoning: boundary.reasoning || boundary.description || 'AI-suggested service boundary.',
    })
  })

  return { components, relationships, serviceBoundaries }
}

/**
 * Transform Data Analysis response into graph data (tables, ERD)
 * @param {Object} dataAnalysisResult - Response from /dataanalysis endpoint
 * @returns {Object} { components: [...], relationships: [...] }
 */
export function transformDataAnalysisToGraph(dataAnalysisResult) {
  const components = []
  const relationships = []

  if (!dataAnalysisResult?.summary) {
    return { components, relationships }
  }

  const summary = dataAnalysisResult.summary

  // Extract ERD entities (tables)
  if (summary.erd?.entities && Array.isArray(summary.erd.entities)) {
    summary.erd.entities.forEach((entity, idx) => {
      components.push({
        id: `cmp_table_${entity.name}`,
        type: 'table',
        name: entity.name,
        language: 'db2',
        path: null,
        metadata: {
          schema: entity.schema || 'DEFAULT',
          columns: entity.attributes?.length || 0,
          attributes: entity.attributes || [],
        },
        tags: entity.is_primary_key ? ['pk'] : [],
      })
    })
  }

  // Extract ERD relationships
  if (summary.erd?.relationships && Array.isArray(summary.erd.relationships)) {
    summary.erd.relationships.forEach((rel, idx) => {
      relationships.push({
        id: `rel_erd_${idx}`,
        from_component_id: `cmp_table_${rel.from_entity}`,
        to_component_id: `cmp_table_${rel.to_entity}`,
        relationship_type: rel.relationship_type === 'one-to-many' ? 'HAS_MANY' : 'REFERENCES',
      })
    })
  }

  // Extract data access patterns (which programs READ/UPDATE which tables)
  if (summary.data_access && Array.isArray(summary.data_access)) {
    summary.data_access.forEach((access, idx) => {
      relationships.push({
        id: `rel_data_${idx}`,
        from_component_id: `cmp_program_${access.program}`,
        to_component_id: `cmp_table_${access.table}`,
        relationship_type: access.operation?.toUpperCase() || 'READS',
      })
    })
  }

  // Extract copybook analysis
  if (summary.copybook_analysis && Array.isArray(summary.copybook_analysis)) {
    summary.copybook_analysis.forEach((copy, idx) => {
      if (!components.find(c => c.id === `cmp_copybook_${copy.name?.replace('.cpy', '')}`)) {
        components.push({
          id: `cmp_copybook_${copy.name?.replace('.cpy', '')}`,
          type: 'copybook',
          name: copy.name,
          language: 'cobol-copybook',
          path: null,
          metadata: {
            total_fields: copy.total_fields || 0,
          },
          tags: [],
        })
      }
    })
  }

  return { components, relationships }
}

/**
 * Transform AI Naming response into naming suggestions format
 * @param {Object} aiNamingResult - Response from /ainaming endpoint
 * @returns {Object} Map of componentId -> { original, suggested, confidence, reasoning }
 */
export function transformAINamingToSuggestions(aiNamingResult) {
  const suggestions = {}

  if (!aiNamingResult?.summary?.suggestions || !Array.isArray(aiNamingResult.summary.suggestions)) {
    return suggestions
  }

  aiNamingResult.summary.suggestions.forEach(suggestion => {
    const componentId = `cmp_program_${suggestion.original_name || suggestion.cobol_name}`
    suggestions[componentId] = {
      original: suggestion.original_name || suggestion.cobol_name,
      suggested: suggestion.suggested_name || suggestion.semantic_name,
      confidence: suggestion.confidence || 0.8,
      reasoning: suggestion.reasoning || suggestion.description || 'AI-suggested semantic name',
    }
  })

  return suggestions
}

/**
 * Merge multiple graph results into unified graph data
 * @param {Object} codeAnalysis - Transformed code analysis data
 * @param {Object} depMapper - Transformed dependency mapper data
 * @param {Object} dataAnalysis - Transformed data analysis data
 * @returns {Object} { components: [...], relationships: [...], serviceBoundaries: [...] }
 */
export function mergeGraphData(codeAnalysis, depMapper, dataAnalysis) {
  const componentMap = new Map()
  const relationshipMap = new Map()
  const serviceBoundaries = depMapper?.serviceBoundaries || []

  // Merge components (use Map to deduplicate by ID)
  const allComponents = [
    ...(codeAnalysis?.components || []),
    ...(depMapper?.components || []),
    ...(dataAnalysis?.components || []),
  ]

  allComponents.forEach(comp => {
    const existing = componentMap.get(comp.id)
    if (existing) {
      // Merge metadata
      componentMap.set(comp.id, {
        ...existing,
        ...comp,
        metadata: { ...existing.metadata, ...comp.metadata },
        tags: [...new Set([...(existing.tags || []), ...(comp.tags || [])])],
      })
    } else {
      componentMap.set(comp.id, comp)
    }
  })

  // Merge relationships (use Map to deduplicate)
  const allRelationships = [
    ...(codeAnalysis?.relationships || []),
    ...(depMapper?.relationships || []),
    ...(dataAnalysis?.relationships || []),
  ]

  allRelationships.forEach(rel => {
    const key = `${rel.from_component_id}_${rel.to_component_id}_${rel.relationship_type}`
    if (!relationshipMap.has(key)) {
      relationshipMap.set(key, rel)
    }
  })

  return {
    components: Array.from(componentMap.values()),
    relationships: Array.from(relationshipMap.values()),
    serviceBoundaries,
  }
}

// ============================================================================
// GRAPH LAYOUT UTILITIES
// ============================================================================

/**
 * Calculate node positions using a grid-based layout optimized for large graphs
 * @param {Array} components - Array of component objects
 * @param {Array} relationships - Array of relationship objects
 * @returns {Object} Map of componentId -> { x, y }
 */
export function calculateNodePositions(components, relationships) {
  const positions = {}
  const NODE_WIDTH = 180
  const NODE_HEIGHT = 80
  const HORIZONTAL_SPACING = NODE_WIDTH + 40
  const VERTICAL_SPACING = NODE_HEIGHT + 30
  const SECTION_GAP = 100

  // Group components by type
  const byType = {
    program: components.filter(c => c.type === 'program'),
    copybook: components.filter(c => c.type === 'copybook'),
    table: components.filter(c => c.type === 'table'),
    queue: components.filter(c => c.type === 'queue'),
    paragraph: components.filter(c => c.type === 'paragraph'),
  }

  // Build adjacency map for dependency-based sorting
  const outgoingCalls = new Map() // program -> programs it calls
  const incomingCalls = new Map() // program -> programs that call it

  relationships
    .filter(r => r.relationship_type === 'CALLS')
    .forEach(r => {
      if (!outgoingCalls.has(r.from_component_id)) outgoingCalls.set(r.from_component_id, new Set())
      outgoingCalls.get(r.from_component_id).add(r.to_component_id)

      if (!incomingCalls.has(r.to_component_id)) incomingCalls.set(r.to_component_id, new Set())
      incomingCalls.get(r.to_component_id).add(r.from_component_id)
    })

  // Find entry points (programs with no incoming CALLS)
  const entryPrograms = byType.program.filter(p => !incomingCalls.has(p.id) || incomingCalls.get(p.id).size === 0)
  const calledPrograms = byType.program.filter(p => incomingCalls.has(p.id) && incomingCalls.get(p.id).size > 0)

  // Sort called programs by number of incoming calls (most connected first)
  calledPrograms.sort((a, b) => {
    const aIn = incomingCalls.get(a.id)?.size || 0
    const bIn = incomingCalls.get(b.id)?.size || 0
    return bIn - aIn
  })

  // Calculate grid dimensions for programs
  // Aim for roughly square layout with some preference for width
  const totalPrograms = byType.program.length
  const COLS_PER_SECTION = Math.max(4, Math.ceil(Math.sqrt(totalPrograms * 1.5)))

  let currentX = 100
  let currentY = 100

  // Layout entry programs in first section (top rows)
  const entryRows = Math.ceil(entryPrograms.length / COLS_PER_SECTION)
  entryPrograms.forEach((prog, idx) => {
    const col = idx % COLS_PER_SECTION
    const row = Math.floor(idx / COLS_PER_SECTION)
    positions[prog.id] = {
      x: currentX + col * HORIZONTAL_SPACING,
      y: currentY + row * VERTICAL_SPACING,
    }
  })

  // Move down for called programs section
  currentY += entryRows * VERTICAL_SPACING + SECTION_GAP

  // Layout called programs in grid below
  calledPrograms.forEach((prog, idx) => {
    const col = idx % COLS_PER_SECTION
    const row = Math.floor(idx / COLS_PER_SECTION)
    positions[prog.id] = {
      x: currentX + col * HORIZONTAL_SPACING,
      y: currentY + row * VERTICAL_SPACING,
    }
  })

  // Calculate where copybooks section starts (to the right)
  const programSectionWidth = COLS_PER_SECTION * HORIZONTAL_SPACING
  const copybookStartX = currentX + programSectionWidth + SECTION_GAP

  // Layout copybooks in a column on the right
  const COPYBOOK_COLS = Math.max(2, Math.ceil(Math.sqrt(byType.copybook.length)))
  byType.copybook.forEach((copy, idx) => {
    const col = idx % COPYBOOK_COLS
    const row = Math.floor(idx / COPYBOOK_COLS)
    positions[copy.id] = {
      x: copybookStartX + col * HORIZONTAL_SPACING,
      y: 100 + row * VERTICAL_SPACING,
    }
  })

  // Layout tables below copybooks
  const copybookRows = Math.ceil(byType.copybook.length / COPYBOOK_COLS)
  const tableStartY = 100 + copybookRows * VERTICAL_SPACING + SECTION_GAP
  byType.table.forEach((table, idx) => {
    const col = idx % COPYBOOK_COLS
    const row = Math.floor(idx / COPYBOOK_COLS)
    positions[table.id] = {
      x: copybookStartX + col * HORIZONTAL_SPACING,
      y: tableStartY + row * VERTICAL_SPACING,
    }
  })

  // Layout queues
  byType.queue.forEach((queue, idx) => {
    positions[queue.id] = {
      x: copybookStartX + (COPYBOOK_COLS + 1) * HORIZONTAL_SPACING,
      y: 100 + idx * VERTICAL_SPACING,
    }
  })

  // Default position for any missed components
  let missedIdx = 0
  components.forEach(comp => {
    if (!positions[comp.id]) {
      positions[comp.id] = {
        x: 100 + (missedIdx % 5) * HORIZONTAL_SPACING,
        y: 100 + Math.floor(missedIdx / 5) * VERTICAL_SPACING,
      }
      missedIdx++
    }
  })

  return positions
}

// ============================================================================
// API ORCHESTRATION FUNCTIONS
// ============================================================================

/**
 * Poll for job completion
 * @param {string} jobType - The job type (e.g., 'codeanalysis', 'dependencymapper')
 * @param {string} jobId - The job ID to poll
 * @param {Function} onProgress - Callback for progress updates
 * @returns {Promise<Object>} The job result when complete
 */
async function pollForCompletion(jobType, jobId, onProgress) {
  let attempts = 0

  while (attempts < MAX_POLL_ATTEMPTS) {
    try {
      const status = await api.getJobStatus(jobType, jobId)

      if (onProgress) {
        onProgress({
          jobType,
          jobId,
          status: status.status,
          progress: status.progress || 0,
          message: status.message || `Running ${jobType}...`,
        })
      }

      if (status.status === 'completed' || status.status === 'complete') {
        return await api.getJobResults(jobType, jobId)
      }

      if (status.status === 'failed' || status.status === 'error') {
        throw new Error(status.error || status.message || `${jobType} job failed`)
      }

      // Wait before next poll
      await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL))
      attempts++
    } catch (error) {
      // If it's a 404, job might not be ready yet - keep polling
      if (error.message?.includes('404')) {
        await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL))
        attempts++
        continue
      }
      throw error
    }
  }

  throw new Error(`Timeout waiting for ${jobType} job to complete`)
}

/**
 * Poll service generation job until completion
 * Uses service-generation-specific endpoints (no /status suffix)
 */
async function pollServiceGenerationCompletion(jobId, onProgress) {
  let attempts = 0

  console.log(' [WorkspaceService] Starting service generation polling:', { jobId })

  while (attempts < MAX_POLL_ATTEMPTS) {
    try {
      // Use service-generation-specific status endpoint (no /status suffix)
      const status = await api.getServiceGenerationStatus(jobId)

      console.log(' [WorkspaceService] Service generation status:', {
        jobId,
        status: status.status,
        attempt: attempts + 1
      })

      if (onProgress) {
        onProgress({
          jobType: 'servicegeneration',
          jobId,
          status: status.status,
          progress: status.progress || 0,
          message: status.message || 'Generating services...',
        })
      }

      if (status.status === 'completed' || status.status === 'complete') {
        console.log(' [WorkspaceService] Service generation completed, fetching results')
        return await api.getServiceGenerationResults(jobId)
      }

      if (status.status === 'failed' || status.status === 'error') {
        // Extract detailed error information
        const errorMsg = status.error_message || status.error || status.message || 'Service generation failed'
        const detailedError = `Service generation failed (Job ID: ${jobId})\n\n` +
          `Error: ${errorMsg}\n\n` +
          `Check API logs for full details or run diagnostics.`

        console.error(' [WorkspaceService] Service generation FAILED:', {
          jobId,
          status: status.status,
          error: errorMsg,
          fullStatus: status
        })

        throw new Error(detailedError)
      }

      // Wait before next poll
      await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL))
      attempts++
    } catch (error) {
      // If it's a 404, job might not be ready yet - keep polling
      if (error.message?.includes('404')) {
        console.log(' [WorkspaceService] Job not ready yet (404), continuing to poll...')
        await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL))
        attempts++
        continue
      }
      throw error
    }
  }

  throw new Error('Timeout waiting for service generation to complete')
}

/**
 * Get user ID from localStorage
 */
function getUserId() {
  const userStr = localStorage.getItem('modernizeit_user')
  if (userStr) {
    try {
      const userData = JSON.parse(userStr)
      return userData.user_id
    } catch (e) {
      console.error('[WorkspaceService] Failed to parse user data:', e)
    }
  }
  return null
}

/**
 * Run the full analysis pipeline (Code Analysis + Dependency Mapper + Data Analysis)
 * Uses direct fetch calls (same pattern as Workflow executor) to avoid abort controller issues.
 *
 * 2026-01-27: Updated to use ingest_job_id instead of source_hash (job-based S3 architecture)
 *
 * @param {string} accountId - The scout_account_id
 * @param {string} applicationName - The application name
 * @param {string} ingestJobId - REQUIRED: Job ID from ingest upload
 * @param {Function} onProgress - Callback for progress updates
 * @returns {Promise<Object>} Merged graph data
 */
export async function runFullAnalysis(accountId, applicationName, ingestJobId, onProgress = null) {
  console.log(' [WorkspaceService] runFullAnalysis STARTED - VERSION: 2026-02-12-POLLING-FIX ')
  const results = {}
  const apiBaseUrl = localStorage.getItem('apiBaseUrl') || getAPIBaseUrl()

  // Get user ID for AWS credentials lookup
  const userId = getUserId()
  if (!userId) {
    return {
      success: false,
      error: 'User not logged in. Please log in to run analysis.',
    }
  }

  // Validate ingest job ID is provided
  if (!ingestJobId) {
    return {
      success: false,
      error: 'No ingest job selected. Please upload files or select an existing upload.',
    }
  }

  // ✅ DIAGNOSTIC: Log ingest job ID
  console.log(' [WorkspaceService] Ingest job completed:', {
    job_id: ingestJobId,
    manifest_url: `${apiBaseUrl}/ingest/${ingestJobId}/manifest`
  })

  try {
    // Step 1: Run Code Analysis
    if (onProgress) onProgress({ step: 'codeAnalysis', status: 'starting', message: 'Starting Code Analysis...' })

    const caResponse = await fetch(`${apiBaseUrl}/codeanalysis`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        scout_account_id: accountId,
        application_name: applicationName,
        user_id: userId,
        ingest_job_id: ingestJobId,  // 2026-01-27: Changed from source_hash to ingest_job_id
        generate_java: false,  // WORKSPACE: Don't generate Java - we only want COBOL source analysis
        generate_graphs: true,
      }),
    })

    if (!caResponse.ok) {
      const errorData = await caResponse.json().catch(() => ({}))
      throw new Error(errorData.detail || `Code Analysis failed: ${caResponse.status}`)
    }

    results.codeAnalysis = await caResponse.json()

    if (results.codeAnalysis.success === false) {
      throw new Error(results.codeAnalysis.error || 'Code Analysis failed')
    }

    // Poll for Code Analysis completion if in worker mode (status QUEUED)
    const caJobId = results.codeAnalysis.job_id
    const caStatus = results.codeAnalysis.status
    console.log(' [WorkspaceService] Code Analysis POST response:', { caJobId, caStatus })

    // Check if job needs polling (NOT already completed/succeeded)
    const caIsTerminal = caStatus === 'completed' || caStatus === 'SUCCEEDED' || caStatus === 'succeeded'
    console.log(' [WorkspaceService] Code Analysis isTerminal:', caIsTerminal, 'will poll:', !caIsTerminal)

    if (!caIsTerminal) {
      console.log(' [WorkspaceService] STARTING POLL for Code Analysis job:', caJobId)
      if (onProgress) onProgress({ step: 'codeAnalysis', status: 'polling', message: 'Waiting for Code Analysis to complete...' })

      const completedJob = await pollJobUntilComplete(caJobId, apiBaseUrl, {
        maxWaitSeconds: 600,
        pollInterval: 2000,
        onProgress: (progress, message) => {
          console.log(' [WorkspaceService] CA polling progress:', progress, message)
          if (onProgress) onProgress({ step: 'codeAnalysis', status: 'running', message })
        }
      })

      // Merge completed job data
      Object.assign(results.codeAnalysis, completedJob)
      console.log(' [WorkspaceService] Code Analysis POLLING COMPLETE:', { caJobId, status: completedJob.status })
    } else {
      console.log(' [WorkspaceService] Code Analysis already terminal, skipping poll:', caStatus)
    }

    // ✅ DIAGNOSTIC: Log Code Analysis job completion
    console.log(' [WorkspaceService] Code Analysis job completed:', {
      job_id: caJobId,
      cross_ref_url: `${apiBaseUrl}/codeanalysis/${caJobId}/results/json/cross_reference.json`
    })

    // WORKSPACE OPTIMIZATION: Skip Dependency Mapper + Data Analysis
    // Reason: Code Analysis already produces COBOL parse results with relationships (CALLS, INCLUDES)
    // Dependency Mapper in worker mode expects Java artifacts (which we're not generating)
    // For COBOL-first Workspace, we only need Code Analysis + ingest manifest
    console.log(' [WorkspaceService] Skipping Dependency Mapper and Data Analysis for Workspace COBOL-first flow')
    console.log(' [WorkspaceService] Will extract COBOL relationships from Code Analysis cross_reference.json')

    // Mark as skipped for clarity
    results.dependencyMapper = { skipped: true, reason: 'Workspace uses Code Analysis parse results for COBOL relationships' }
    results.dataAnalysis = { skipped: true, reason: 'Not needed for initial Workspace COBOL view' }

    console.log(' [WorkspaceService] ===== CODE ANALYSIS COMPLETE, NOW FETCHING ARTIFACTS =====')
    console.log(' [WorkspaceService] Final job statuses:', {
      ca: results.codeAnalysis?.status,
      dm: 'skipped',
      da: 'skipped'
    })

    // Step 4: Fetch detailed artifact data from Code Analysis and Ingest
    if (onProgress) onProgress({ step: 'fetchArtifacts', status: 'processing', message: 'Fetching analysis artifacts...' })

    const artifactData = {
      codeAnalysis: {},  // Add Code Analysis cross_reference for COBOL relationships
      ingest: {},        // Add ingest manifest for COBOL source inventory
    }

    // Step 4a: Fetch ingest manifest (COBOL source files inventory)
    console.log(' [WorkspaceService] Fetching ingest manifest for source inventory:', ingestJobId)
    try {
      const manifestUrl = `${apiBaseUrl}/ingest/${ingestJobId}/manifest`
      console.log(' [WorkspaceService] Fetching ingest manifest:', manifestUrl)
      const manifestResponse = await fetch(manifestUrl)

      if (manifestResponse.ok) {
        artifactData.ingest.manifest = await manifestResponse.json()
        console.log(' [WorkspaceService] Fetched ingest manifest:', {
          totalFiles: artifactData.ingest.manifest?.files?.length || 0,
          files: artifactData.ingest.manifest?.files?.map(f => f.path) || []
        })

        // ✅ DIAGNOSTIC: Log manifest fetch success
        console.log(' [WorkspaceService] Manifest fetched successfully:', {
          total_files: artifactData.ingest.manifest?.files?.length || 0,
          url_used: manifestUrl
        })
      } else {
        console.warn(' [WorkspaceService] Failed to fetch ingest manifest:', manifestResponse.status)
      }
    } catch (manifestError) {
      console.warn(' [WorkspaceService] Error fetching ingest manifest:', manifestError)
    }

    // Step 4b: Fetch Code Analysis cross_reference.json (for COBOL relationships)
    console.log(' [WorkspaceService] Fetching Code Analysis cross_reference for COBOL relationships:', caJobId)
    try {
      const xrefUrl = `${apiBaseUrl}/codeanalysis/${caJobId}/results/json/cross_reference.json`
      console.log(' [WorkspaceService] Fetching cross_reference:', xrefUrl)
      const xrefResponse = await fetch(xrefUrl)
      console.log(' [WorkspaceService] cross_reference response:', { status: xrefResponse.status, ok: xrefResponse.ok })

      if (xrefResponse.ok) {
        artifactData.codeAnalysis.cross_reference = await xrefResponse.json()
        console.log(' [WorkspaceService] Fetched cross_reference.json:', {
          copybook_usage: Object.keys(artifactData.codeAnalysis.cross_reference?.copybook_usage || {}).length,
          call_graph: Object.keys(artifactData.codeAnalysis.cross_reference?.call_graph || {}).length,
          file_usage: Object.keys(artifactData.codeAnalysis.cross_reference?.file_usage || {}).length,
        })

        // ✅ DIAGNOSTIC: Log cross_reference fetch success
        console.log(' [WorkspaceService] Cross reference fetched successfully:', {
          programs: Object.keys(artifactData.codeAnalysis.cross_reference?.call_graph || {}).length,
          copybooks: Object.keys(artifactData.codeAnalysis.cross_reference?.copybook_usage || {}).length,
          url_used: xrefUrl
        })
      } else {
        console.warn(' [WorkspaceService] Failed to fetch cross_reference.json:', xrefResponse.status)
      }
    } catch (xrefError) {
      console.warn(' [WorkspaceService] Error fetching cross_reference.json:', xrefError)
    }

    // Step 5: Transform and merge all results
    if (onProgress) onProgress({ step: 'transform', status: 'processing', message: 'Processing results...' })

    console.log('[WorkspaceService] Transforming with artifact data available:', {
      hasIngestManifest: !!artifactData.ingest.manifest,
      hasCrossReference: !!artifactData.codeAnalysis.cross_reference,
    })

    // WORKSPACE: Build components from INGEST MANIFEST (COBOL source files only)
    console.log(' [WorkspaceService] Building source inventory from ingest manifest (COBOL files only)')
    const sourceComponents = transformIngestManifestToComponents(artifactData.ingest.manifest || {})

    console.log('[WorkspaceService] Source Components (from ingest manifest):', {
      components: sourceComponents.components?.length || 0,
      programs: sourceComponents.components?.filter(c => c.type === 'program').length || 0,
      copybooks: sourceComponents.components?.filter(c => c.type === 'copybook').length || 0,
      jcl: sourceComponents.components?.filter(c => c.type === 'jcl').length || 0,
      data: sourceComponents.components?.filter(c => c.type === 'data').length || 0,
    })

    // WORKSPACE: Extract COBOL relationships from Code Analysis cross_reference
    // ✅ Pass components array to build alias map for ID resolution
    console.log(' [WorkspaceService] Extracting COBOL relationships from Code Analysis cross_reference')
    const cobolRelationships = extractCobolRelationships(
      artifactData.codeAnalysis.cross_reference || null,
      sourceComponents.components || []  // ✅ Pass components for alias mapping
    )

    console.log('[WorkspaceService] COBOL Relationships (from Code Analysis):', {
      includes: cobolRelationships.relationships?.filter(r => r.relationship_type === 'INCLUDES').length || 0,  // ✅ Changed to relationship_type
      calls: cobolRelationships.relationships?.filter(r => r.relationship_type === 'CALLS').length || 0,       // ✅ Changed to relationship_type
      total: cobolRelationships.relationships?.length || 0,
    })

    // FALLBACK: If manifest returned 0 components but we have relationships, build nodes from relationships
    if (sourceComponents.components.length === 0 && artifactData.codeAnalysis.cross_reference) {
      console.warn('[WorkspaceService]   Manifest returned 0 components! Building fallback nodes from relationships...')

      const xref = artifactData.codeAnalysis.cross_reference
      const fallbackComponents = []
      const seenNames = new Set()

      // Extract programs from call_graph
      if (xref.call_graph) {
        Object.keys(xref.call_graph).forEach(programFile => {
          if (!seenNames.has(programFile)) {
            seenNames.add(programFile)
            // Extract basename without extension
            const baseName = programFile.replace(/\.(cbl|CBL|cob|COB)$/i, '')
            // ✅ Use "program:" prefix to avoid conflict with file-based IDs
            fallbackComponents.push({
              id: `program:${baseName}`,  // ✅ Logical program ID (not file-based)
              type: 'program',
              name: programFile,
              language: 'cobol',
              path: programFile,
              metadata: {
                source: 'relationship_fallback',
                basename: baseName,
              },
              tags: []
            })
          }
        })

        // Also add called programs
        Object.values(xref.call_graph).flat().forEach(calledProgramFile => {
          if (!seenNames.has(calledProgramFile)) {
            seenNames.add(calledProgramFile)
            // Extract basename without extension
            const baseName = calledProgramFile.replace(/\.(cbl|CBL|cob|COB)$/i, '')
            // ✅ Use "program:" prefix to avoid conflict with file-based IDs
            fallbackComponents.push({
              id: `program:${baseName}`,  // ✅ Logical program ID (not file-based)
              type: 'program',
              name: calledProgramFile,
              language: 'cobol',
              path: calledProgramFile,
              metadata: {
                source: 'relationship_fallback',
                basename: baseName,
              },
              tags: []
            })
          }
        })
      }

      // Extract copybooks from copybook_usage
      if (xref.copybook_usage) {
        Object.keys(xref.copybook_usage).forEach(copybookFile => {
          if (!seenNames.has(copybookFile)) {
            seenNames.add(copybookFile)
            // Extract basename without extension
            const baseName = copybookFile.replace(/\.(cpy|CPY|copy|COPY)$/i, '')
            // ✅ Use "copybook:" prefix to avoid conflict with file-based IDs
            fallbackComponents.push({
              id: `copybook:${baseName}`,  // ✅ Logical copybook ID (not file-based)
              type: 'copybook',
              name: copybookFile,
              language: 'cobol-copybook',
              path: copybookFile,
              metadata: {
                source: 'relationship_fallback',
                basename: baseName,
              },
              tags: []
            })
          }
        })
      }

      console.log(`[WorkspaceService] Built ${fallbackComponents.length} fallback components from relationships:`, {
        programs: fallbackComponents.filter(c => c.type === 'program').length,
        copybooks: fallbackComponents.filter(c => c.type === 'copybook').length
      })

      sourceComponents.components = fallbackComponents

      // ✅ NOW re-extract relationships with updated components for proper alias mapping
      console.log(' [WorkspaceService] Re-extracting relationships with fallback components for alias mapping')
      const updatedRelationships = extractCobolRelationships(
        artifactData.codeAnalysis.cross_reference || null,
        fallbackComponents  // Use fallback components for alias map
      )
      cobolRelationships.relationships = updatedRelationships.relationships
    }

    // ✅ DEDUPLICATION: Detect and log duplicate component IDs
    const componentIdMap = new Map()
    const duplicates = []
    sourceComponents.components.forEach(comp => {
      if (componentIdMap.has(comp.id)) {
        duplicates.push({
          id: comp.id,
          type: comp.type,
          name: comp.name,
          path: comp.path,
          source: comp.metadata?.source
        })
      } else {
        componentIdMap.set(comp.id, comp)
      }
    })

    if (duplicates.length > 0) {
      console.error(' [WorkspaceService]  DUPLICATE COMPONENT IDs DETECTED:', {
        totalComponents: sourceComponents.components.length,
        uniqueComponents: componentIdMap.size,
        duplicateCount: duplicates.length,
        duplicates: duplicates.slice(0, 10),  // Show first 10
      })
    } else {
      console.log(' [WorkspaceService]  Component ID uniqueness verified:', {
        totalComponents: sourceComponents.components.length,
        uniqueComponents: componentIdMap.size,
        duplicates: 0
      })
    }

    // WORKSPACE: Merge source components (from ingest) with relationships (from Code Analysis)
    const mergedGraph = {
      components: Array.from(componentIdMap.values()),  // ✅ Use deduplicated components
      relationships: cobolRelationships.relationships || [], // COBOL relationships (CALLS, INCLUDES)
      serviceBoundaries: [],                                // No service boundaries yet (could add later)
    }

    const positions = calculateNodePositions(mergedGraph.components, mergedGraph.relationships)

    console.log(' [WorkspaceService] Merged graph (COBOL source + COBOL relationships):', {
      components: mergedGraph.components?.length || 0,
      programs: mergedGraph.components?.filter(c => c.type === 'program').length || 0,
      copybooks: mergedGraph.components?.filter(c => c.type === 'copybook').length || 0,
      jcl: mergedGraph.components?.filter(c => c.type === 'jcl').length || 0,
      data: mergedGraph.components?.filter(c => c.type === 'data').length || 0,
      relationships: mergedGraph.relationships?.length || 0,
      includes: mergedGraph.relationships?.filter(r => r.relationship_type === 'INCLUDES').length || 0,  // ✅ Changed to relationship_type
      calls: mergedGraph.relationships?.filter(r => r.relationship_type === 'CALLS').length || 0,       // ✅ Changed to relationship_type
      positions: Object.keys(positions).length,
    })

    // ✅ Verify edges can be built from relationships (data-level verification)
    console.log(' [WorkspaceEdges] Final edge verification before returning to UI:', {
      totalRelationships: mergedGraph.relationships?.length || 0,
      sampleRelationships: mergedGraph.relationships?.slice(0, 5).map(r => ({
        id: r.id,
        from: r.from_component_id,
        to: r.to_component_id,
        type: r.relationship_type
      })),
    })

    // ✅ Build nodeMap to verify all edge endpoints exist
    const nodeMap = new Map()
    mergedGraph.components.forEach(comp => nodeMap.set(comp.id, comp))

    let missingEndpoints = 0
    const missingExamples = []
    mergedGraph.relationships?.forEach(rel => {
      if (!nodeMap.has(rel.from_component_id)) {
        missingEndpoints++
        if (missingExamples.length < 3) missingExamples.push(`source: ${rel.from_component_id}`)
      }
      if (!nodeMap.has(rel.to_component_id)) {
        missingEndpoints++
        if (missingExamples.length < 3) missingExamples.push(`target: ${rel.to_component_id}`)
      }
    })

    console.log(' [WorkspaceEdges] Edge endpoint validation:', {
      totalNodes: nodeMap.size,
      totalRelationships: mergedGraph.relationships?.length || 0,
      missingEndpoints: missingEndpoints,
      missingExamples: missingExamples.length > 0 ? missingExamples : 'None - all endpoints valid! ✅',
    })

    if (onProgress) onProgress({ step: 'complete', status: 'complete', message: 'Analysis complete!' })

    return {
      success: true,
      inventory: {
        schema_version: '1.0',
        job_id: results.codeAnalysis?.job_id,
        components: mergedGraph.components,
      },
      relationships: {
        relationships: mergedGraph.relationships,
      },
      serviceBoundaries: mergedGraph.serviceBoundaries,
      positions: positions,
      rawResults: results,
    }

  } catch (error) {
    console.error('[WorkspaceService] Analysis failed:', error)
    if (onProgress) onProgress({ step: 'error', status: 'error', message: error.message })

    return {
      success: false,
      error: error.message,
      rawResults: results,
    }
  }
}

/**
 * Generate Java Monolith - Run Code Analysis with Java generation enabled
 *
 * This is a prerequisite for Service Generation.
 * Produces: generated_java.zip + java_monolith_uri in manifest
 *
 * @param {string} accountId - The scout_account_id
 * @param {string} applicationName - The application name
 * @param {string} ingestJobId - The completed ingest job ID
 * @param {Function} onProgress - Callback for progress updates
 * @returns {Promise<Object>} Result with success flag and job details
 */
export async function runJavaMonolithGeneration(accountId, applicationName, ingestJobId, onProgress = null) {
  console.log(' [WorkspaceService] Java Monolith Generation STARTED')
  const apiBaseUrl = localStorage.getItem('apiBaseUrl') || getAPIBaseUrl()

  // Get user ID for AWS credentials lookup
  const userId = getUserId()
  if (!userId) {
    return {
      success: false,
      error: 'User not logged in. Please log in to generate Java.',
    }
  }

  // Validate ingest job ID is provided
  if (!ingestJobId) {
    return {
      success: false,
      error: 'No ingest job selected. Please upload files or select an existing upload.',
    }
  }

  try {
    if (onProgress) onProgress({ step: 'javaGeneration', status: 'starting', message: 'Starting Java generation...' })

    // Call Code Analysis with generate_java: true
    const response = await fetch(`${apiBaseUrl}/codeanalysis`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        scout_account_id: accountId,
        application_name: applicationName,
        user_id: userId,
        ingest_job_id: ingestJobId,
        generate_java: true,          // KEY DIFFERENCE from runFullAnalysis
        generate_graphs: false,       // We don't need graphs again
      }),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.detail || `Java generation failed: ${response.status}`)
    }

    const result = await response.json()

    if (result.success === false) {
      throw new Error(result.error || 'Java generation failed')
    }

    // Poll for completion if in worker mode
    const jobId = result.job_id
    const status = result.status
    console.log(' [WorkspaceService] Java generation POST response:', { jobId, status })

    const isTerminal = status === 'completed' || status === 'SUCCEEDED' || status === 'succeeded'

    if (!isTerminal) {
      console.log(' [WorkspaceService] Polling for Java generation completion:', jobId)
      if (onProgress) onProgress({ step: 'javaGeneration', status: 'polling', message: 'Waiting for Java generation...' })

      const completedJob = await pollJobUntilComplete(jobId, apiBaseUrl, {
        maxWaitSeconds: 600,
        pollInterval: 2000,
        onProgress: (progress, message) => {
          console.log(' [WorkspaceService] Java gen polling progress:', progress, message)
          if (onProgress) onProgress({ step: 'javaGeneration', status: 'running', message })
        }
      })

      Object.assign(result, completedJob)
      console.log(' [WorkspaceService] Java generation COMPLETE:', { jobId, status: completedJob.status })
    }

    // Verify java_monolith_uri exists in output
    const hasJavaMonolith = result.output_data?.java_monolith_uri || result.output_data?.generated_java_zip_uri
    console.log(' [WorkspaceService] Java monolith URI:', hasJavaMonolith ? 'FOUND' : 'MISSING')

    if (!hasJavaMonolith) {
      console.warn(' [WorkspaceService] WARNING: Code Analysis completed but no java_monolith_uri found')
    }

    return {
      success: true,
      jobId: jobId,
      javaMonolithUri: hasJavaMonolith,
      result,
    }

  } catch (error) {
    console.error(' [WorkspaceService] Java generation error:', error)
    return {
      success: false,
      error: error.message || 'Java generation failed',
    }
  }
}

/**
 * Detect if Java monolith already exists for this project.
 *
 * Checks completed Code Analysis jobs for java_monolith_uri in output_data.
 * Handles: pre-existing projects, Workflow-generated Java, any prior run.
 *
 * @param {string} accountId - The scout_account_id
 * @param {string} applicationName - The application name
 * @returns {Promise<{found: boolean, jobId: string|null}>}
 */
export async function detectExistingJavaMonolith(accountId, applicationName) {
  const apiBaseUrl = localStorage.getItem('apiBaseUrl') || getAPIBaseUrl()
  const token = localStorage.getItem('modernizeit_token')
  const headers = {}
  if (token) headers['Authorization'] = `Bearer ${token}`

  try {
    // Step 1: List completed code analysis jobs for this app
    const listUrl = `${apiBaseUrl}/jobs/list?account_id=${encodeURIComponent(accountId)}&application_name=${encodeURIComponent(applicationName)}&flow_type=codeanalysis&status=completed&limit=5`
    const listResp = await fetch(listUrl, { headers })

    if (!listResp.ok) {
      console.warn(' [WorkspaceService] Failed to list CA jobs for Java detection:', listResp.status)
      return { found: false, jobId: null }
    }

    const listData = await listResp.json()
    const caJobs = listData.jobs || []
    console.log(' [WorkspaceService] Java detection: found', caJobs.length, 'completed CA jobs')

    if (caJobs.length === 0) return { found: false, jobId: null }

    // Step 2: Check each job's output_data for java_monolith_uri (newest first)
    for (const job of caJobs) {
      const jobUrl = `${apiBaseUrl}/jobs/${job.job_id}`
      const jobResp = await fetch(jobUrl, { headers })

      if (!jobResp.ok) continue

      const jobData = await jobResp.json()
      const outputData = jobData.output_data

      if (outputData && (outputData.java_monolith_uri || outputData.generated_java_zip_uri)) {
        console.log(' [WorkspaceService] Java monolith DETECTED in job:', job.job_id)
        return { found: true, jobId: job.job_id }
      }
    }

    console.log(' [WorkspaceService] No Java monolith found in any CA job')
    return { found: false, jobId: null }

  } catch (error) {
    console.warn(' [WorkspaceService] Java detection error (non-fatal):', error.message)
    return { found: false, jobId: null }
  }
}

/**
 * Run AI Naming flow
 * @param {string} accountId - The scout_account_id
 * @param {string} applicationName - The application name
 * @param {Function} onProgress - Callback for progress updates
 * @returns {Promise<Object>} AI naming suggestions
 */
export async function runAINaming(accountId, applicationName, onProgress = null) {
  try {
    if (onProgress) onProgress({ step: 'aiNaming', status: 'starting', message: 'Running AI Naming...' })

    // Note: The API endpoint may need to be adjusted based on actual implementation
    const response = await api.startCodeRefactor({
      scoutAccountId: accountId,
      applicationName: applicationName,
      mode: 'analyze', // AI naming is part of refactor analysis
      useAI: true,
    })

    let result
    if (response.job_id) {
      result = await pollForCompletion('coderefactor', response.job_id, onProgress)
    } else {
      result = response
    }

    const suggestions = transformAINamingToSuggestions(result)

    if (onProgress) onProgress({ step: 'complete', status: 'complete', message: 'AI Naming complete!' })

    return {
      success: true,
      suggestions,
      rawResult: result,
    }

  } catch (error) {
    console.error('[WorkspaceService] AI Naming failed:', error)
    if (onProgress) onProgress({ step: 'error', status: 'error', message: error.message })

    return {
      success: false,
      error: error.message,
    }
  }
}

/**
 * Check if AWS credentials are configured for the current user
 * @returns {Promise<boolean>} True if credentials exist, false otherwise
 */
async function checkAWSCredentials() {
  try {
    const credentials = await db.getAWSCredentials()
    // Backend returns null or undefined if no credentials
    return credentials !== null && credentials !== undefined
  } catch (error) {
    console.error(' [WorkspaceService] Failed to check AWS credentials:', error)

    // Distinguish auth errors (401/403) from missing credentials
    if (error.message && (
      error.message.includes('401') ||
      error.message.includes('403') ||
      error.message.includes('Authorization header required') ||
      error.message.includes('Not authenticated')
    )) {
      // Authentication problem - user needs to login
      throw new Error('NOT_AUTHENTICATED|You are not authenticated. Please login again to continue.')
    }

    // For other errors (network, etc), assume no credentials to be safe
    return false
  }
}

/**
 * Run Service Generation with scoped components
 * @param {string} accountId - The scout_account_id
 * @param {string} applicationName - The application name
 * @param {string} serviceName - The name for the generated service
 * @param {Array} scopeItems - Array of { id, name, type } for components in scope
 * @param {Function} onProgress - Callback for progress updates
 * @returns {Promise<Object>} Service generation result
 */
export async function runServiceGeneration(accountId, applicationName, serviceName, scopeItems, onProgress = null) {
  try {
    if (onProgress) onProgress({ step: 'serviceGeneration', status: 'starting', message: 'Checking prerequisites...' })

    // Get user_id from localStorage (required for AWS credentials lookup)
    const userDataStr = localStorage.getItem('modernizeit_user')
    if (!userDataStr) {
      throw new Error('User not authenticated. Please log in again.')
    }
    const userData = JSON.parse(userDataStr)
    const userId = userData.user_id

    if (!userId) {
      throw new Error('User ID not found in session. Please log in again.')
    }

    console.log(' [WorkspaceService] Service Generation request:', {
      accountId,
      applicationName,
      serviceName,
      userId: userId.substring(0, 8) + '...',  // Log partial ID only
      scopeItemCount: scopeItems.length
    })

    // Credential validation handled by backend (same as Workflow path)
    // Backend resolves: DB creds (via user_id) → JWT creds → env creds

    if (onProgress) onProgress({ step: 'serviceGeneration', status: 'starting', message: 'Generating service...' })

    // Extract program names from scope
    const programNames = scopeItems
      .filter(item => item.type === 'program')
      .map(item => item.name)

    // Use first program as main, or the service name
    const mainProgram = programNames[0] || serviceName

    const response = await api.startServiceGeneration({
      scoutAccountId: accountId,
      applicationName: applicationName,
      userId: userId,  // ✅ ADD user_id for AWS credentials lookup
      programName: mainProgram,
      basePackage: `com.modernizeit.${serviceName.toLowerCase()}`,
      includeAllRepositories: true,
      includeAllFields: true,
      strictValidation: true,
    })

    let result
    if (response.job_id) {
      // Use service-generation-specific polling (different endpoint structure than generic jobs)
      result = await pollServiceGenerationCompletion(response.job_id, onProgress)
    } else {
      result = response
    }

    if (onProgress) onProgress({ step: 'complete', status: 'complete', message: 'Service generated!' })

    return {
      success: true,
      jobId: result.job_id,
      result,
    }

  } catch (error) {
    console.error(' [WorkspaceService] Service Generation failed:', error)
    console.error(' [WorkspaceService] Error details:', {
      message: error.message,
      stack: error.stack?.substring(0, 200)
    })

    if (onProgress) onProgress({ step: 'error', status: 'error', message: error.message })

    return {
      success: false,
      error: error.message,
    }
  }
}

/**
 * Upload COBOL ZIP file to ingest endpoint
 * @param {string} accountId - The scout_account_id
 * @param {string} applicationName - The application name
 * @param {string} userId - The user ID for AWS credentials
 * @param {File} file - The ZIP file to upload
 * @param {Function} onProgress - Callback for progress updates
 * @returns {Promise<Object>} Upload result
 */
export async function uploadCobolFiles(accountId, applicationName, userId, file, onProgress = null) {
  let ingestJobId = null
  try {
    if (onProgress) onProgress({ step: 'upload', status: 'starting', message: 'Uploading files...' })

    // Create FormData for multipart upload
    const formData = new FormData()
    formData.append('file', file)
    formData.append('scout_account_id', accountId)
    formData.append('application_name', applicationName)
    formData.append('user_id', userId)
    formData.append('generate_type_mappings', 'true')
    formData.append('source_lang', 'cobol')
    formData.append('target_lang', 'java')

    // Get API base URL
    const apiBaseUrl = localStorage.getItem('apiBaseUrl') || getAPIBaseUrl()

    // Use fetch directly for multipart form upload (not JSON)
    const response = await fetch(`${apiBaseUrl}/ingest/upload`, {
      method: 'POST',
      body: formData,
      // Don't set Content-Type - browser will set it with boundary for multipart
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.detail || `Upload failed with status ${response.status}`)
    }

    const result = await response.json()
    ingestJobId = result.job_id

    console.log(` [WorkspaceService] Ingest job accepted: ${ingestJobId}`)

    // Worker-mode: Poll ingest job until worker completes (SUCCEEDED/FAILED).
    // Without this, UI shows "Upload complete" while worker is still processing
    // (or has failed), causing confusing errors when Analyze runs later.
    if (onProgress) onProgress({ step: 'ingest', status: 'polling', message: 'Processing uploaded files...' })

    console.log(` [WorkspaceService] Starting poll for ingest job: ${ingestJobId}`)

    const completedJob = await pollJobUntilComplete(ingestJobId, apiBaseUrl, {
      maxWaitSeconds: 300,
      pollInterval: 2000,
      onProgress: (progress, message) => {
        if (onProgress) onProgress({ step: 'ingest', status: 'running', message: message || 'Processing uploaded files...' })
      }
    })

    console.log(` [WorkspaceService] Ingest poll complete: ${ingestJobId} → ${completedJob.status}`)

    if (onProgress) onProgress({ step: 'complete', status: 'complete', message: 'Upload complete!' })

    return {
      success: true,
      ingestJobId,
      jobId: ingestJobId,
      result: completedJob,
    }

  } catch (error) {
    const jobRef = ingestJobId ? ` (job: ${ingestJobId})` : ''
    console.error(` [WorkspaceService] Upload/ingest failed${jobRef}:`, error)
    if (onProgress) onProgress({ step: 'error', status: 'error', message: error.message })

    return {
      success: false,
      ingestJobId,
      error: `Upload failed${jobRef}: ${error.message}`,
    }
  }
}

// ============================================================================
// WORKBOOK PERSISTENCE API
// ============================================================================

/**
 * Create a new workbook
 * @param {string} accountId - Account ID
 * @param {string} userId - User ID
 * @param {string} workbookName - Name for the workbook
 * @param {string} description - Optional description
 * @param {string} applicationId - Optional application ID
 * @returns {Promise<Object>} Created workbook
 */
export async function createWorkbook(accountId, userId, workbookName, description = null, applicationId = null) {
  const apiBaseUrl = localStorage.getItem('apiBaseUrl') || getAPIBaseUrl()

  const response = await fetch(`${apiBaseUrl}/workspace/workbooks`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      account_id: accountId,
      user_id: userId,
      workbook_name: workbookName,
      description,
      application_id: applicationId,
    }),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.detail || `Failed to create workbook: ${response.status}`)
  }

  return await response.json()
}

/**
 * List workbooks for an account
 * @param {string} accountId - Account ID
 * @param {string} status - Optional filter by status
 * @param {string} userId - Optional filter by user
 * @returns {Promise<Array>} List of workbooks
 */
export async function listWorkbooks(accountId, status = null, userId = null) {
  const apiBaseUrl = localStorage.getItem('apiBaseUrl') || getAPIBaseUrl()

  let url = `${apiBaseUrl}/workspace/workbooks/${accountId}`
  const params = new URLSearchParams()
  if (status) params.append('status', status)
  if (userId) params.append('user_id', userId)
  if (params.toString()) url += `?${params.toString()}`

  const response = await fetch(url)

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.detail || `Failed to list workbooks: ${response.status}`)
  }

  return await response.json()
}

/**
 * Get workbook with all items
 * @param {string} accountId - Account ID
 * @param {string} workbookId - Workbook ID
 * @returns {Promise<Object>} Workbook with items
 */
export async function getWorkbook(accountId, workbookId) {
  const apiBaseUrl = localStorage.getItem('apiBaseUrl') || getAPIBaseUrl()

  const response = await fetch(`${apiBaseUrl}/workspace/workbooks/${accountId}/${workbookId}`)

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.detail || `Failed to get workbook: ${response.status}`)
  }

  return await response.json()
}

/**
 * Update workbook metadata
 * @param {string} workbookId - Workbook ID
 * @param {Object} updates - Fields to update (workbook_name, description, status)
 * @returns {Promise<Object>} Updated workbook
 */
export async function updateWorkbook(workbookId, updates) {
  const apiBaseUrl = localStorage.getItem('apiBaseUrl') || getAPIBaseUrl()

  const response = await fetch(`${apiBaseUrl}/workspace/workbooks/${workbookId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.detail || `Failed to update workbook: ${response.status}`)
  }

  return await response.json()
}

/**
 * Delete a workbook
 * @param {string} workbookId - Workbook ID
 * @returns {Promise<Object>} Success message
 */
export async function deleteWorkbook(workbookId) {
  const apiBaseUrl = localStorage.getItem('apiBaseUrl') || getAPIBaseUrl()

  const response = await fetch(`${apiBaseUrl}/workspace/workbooks/${workbookId}`, {
    method: 'DELETE',
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.detail || `Failed to delete workbook: ${response.status}`)
  }

  return await response.json()
}

/**
 * Add item to workbook
 * @param {string} workbookId - Workbook ID
 * @param {Object} item - Item data (program_name, paragraph_name, paragraph_id, line_start, line_end)
 * @returns {Promise<Object>} Created item
 */
export async function addWorkbookItem(workbookId, item) {
  const apiBaseUrl = localStorage.getItem('apiBaseUrl') || getAPIBaseUrl()

  const response = await fetch(`${apiBaseUrl}/workspace/workbooks/${workbookId}/items`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(item),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.detail || `Failed to add item: ${response.status}`)
  }

  return await response.json()
}

/**
 * Update workbook item status
 * @param {string} itemId - Item ID
 * @param {string} status - New status (pending, in-progress, converted, error)
 * @param {string} notes - Optional notes
 * @returns {Promise<Object>} Updated item
 */
export async function updateWorkbookItemStatus(itemId, status, notes = null) {
  const apiBaseUrl = localStorage.getItem('apiBaseUrl') || getAPIBaseUrl()

  const response = await fetch(`${apiBaseUrl}/workspace/workbooks/items/${itemId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status, notes }),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.detail || `Failed to update item: ${response.status}`)
  }

  return await response.json()
}

/**
 * Remove item from workbook
 * @param {string} itemId - Item ID
 * @returns {Promise<Object>} Success message
 */
export async function removeWorkbookItem(itemId) {
  const apiBaseUrl = localStorage.getItem('apiBaseUrl') || getAPIBaseUrl()

  const response = await fetch(`${apiBaseUrl}/workspace/workbooks/items/${itemId}`, {
    method: 'DELETE',
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.detail || `Failed to remove item: ${response.status}`)
  }

  return await response.json()
}

/**
 * Fetch COBOL source file content for Code Editor
 * @param {string} accountId - Account ID
 * @param {string} applicationName - Application name
 * @param {string} filePath - Relative file path (e.g., "src/CUSTVAL.cbl")
 * @returns {Promise<Object>} File content data
 */
export async function fetchFileContent(accountId, applicationName, filePath) {
  const apiBaseUrl = localStorage.getItem('apiBaseUrl') || getAPIBaseUrl()
  const url = `${apiBaseUrl}/workspace/files/${accountId}/${applicationName}?path=${encodeURIComponent(filePath)}`

  console.log(' [workspaceService] Fetching file content:', {
    accountId,
    applicationName,
    filePath,
    url
  })

  try {
    const response = await fetch(url)

    if (!response.ok) {
      console.error(' [workspaceService] File fetch failed:', response.status)
      throw new Error(`HTTP ${response.status}: ${response.statusText}`)
    }

    const data = await response.json()

    console.log(' [workspaceService] File loaded successfully:', {
      path: data.path,
      size: data.size,
      line_count: data.line_count,
      language: data.language
    })

    return data
  } catch (error) {
    console.error(' [workspaceService] Error fetching file content:', error)
    throw error
  }
}

// ============================================================================
// SERVICE GENERATION RESULTS + DOWNLOAD
// ============================================================================

export async function fetchServiceGenResults(jobId) {
  return await api.getServiceGenerationResults(jobId)
}

export async function downloadServiceZip(jobId) {
  return await api.downloadServiceGeneration(jobId)
}

// ============================================================================
// COBOL FILE READING + PROCEDURE MODEL (for IDE Target tab)
// ============================================================================

/**
 * Read a COBOL source file using the editor endpoint (works with S3/worker mode)
 */
export async function fetchCobolFileForWorkspace(ingestJobId, relativePath) {
  const apiBaseUrl = localStorage.getItem('apiBaseUrl') || getAPIBaseUrl()
  const token = localStorage.getItem('modernizeit_token')
  const headers = {}
  if (token) headers['Authorization'] = `Bearer ${token}`

  const url = `${apiBaseUrl}/editor/cobol/file?job_id=${encodeURIComponent(ingestJobId)}&path=${encodeURIComponent(relativePath)}`
  const response = await fetch(url, { headers })
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }))
    throw new Error(error.detail || `Failed to fetch COBOL file: ${response.status}`)
  }
  const data = await response.json()
  return data.content
}

/**
 * Fetch procedure_model.json for a code analysis job (paragraphs with line numbers)
 */
export async function fetchProcedureModel(jobId) {
  const { getJobBaseName, getProcedureModel } = await import('./codeAnalysisService.js')
  const baseName = await getJobBaseName(jobId)
  if (!baseName) {
    console.warn(' [workspaceService] Could not determine baseName for procedure model')
    return null
  }
  return await getProcedureModel(jobId, baseName)
}

// ============================================================================
// EXPORT DEFAULT
// ============================================================================

export default {
  // Transformations
  transformCodeAnalysisToGraph,
  transformDependencyMapperToGraph,
  transformDataAnalysisToGraph,
  transformAINamingToSuggestions,
  mergeGraphData,
  calculateNodePositions,

  // API Orchestration
  runFullAnalysis,
  runJavaMonolithGeneration,
  detectExistingJavaMonolith,
  runAINaming,
  runServiceGeneration,
  uploadCobolFiles,

  // Workbook Persistence
  createWorkbook,
  listWorkbooks,
  getWorkbook,
  updateWorkbook,
  deleteWorkbook,
  addWorkbookItem,
  updateWorkbookItemStatus,
  removeWorkbookItem,

  // File Operations
  fetchFileContent,

  // Service Generation Results
  fetchServiceGenResults,
  downloadServiceZip,

  // COBOL File Reading + Procedure Model
  fetchCobolFileForWorkspace,
  fetchProcedureModel,
}


// ── Fix & Continue File Tree API ───────────────────────────────────────────────
// These functions centralize all editor file-tree API calls for the ProjectExplorer.
// They use the same auth + base URL pattern as the rest of this service.

function _editorHeaders() {
  const token = localStorage.getItem('modernizeit_token')
  const headers = { 'Content-Type': 'application/json' }
  if (token) headers['Authorization'] = `Bearer ${token}`
  return headers
}

function _editorBase() {
  return localStorage.getItem('apiBaseUrl') || getAPIBaseUrl()
}

/**
 * List all files in the working project as a merged base+overlay tree.
 * @param {string} gateJobId   — pre_flight or java_packaging job ID
 * @param {string} scope       — 'focused' | 'full'
 * @param {object} [opts]      — optional { upstreamSvcgenJobId, upstreamJvalJobId }
 * @returns {Promise<Array>}   — [{ path, type: 'file'|'dir', source: 'base'|'overlay'|'created' }]
 */
export async function editorListFiles(gateJobId, scope = 'focused', opts = {}) {
  const base = _editorBase()
  const params = new URLSearchParams({ gate_job_id: gateJobId, scope })
  if (opts.upstreamSvcgenJobId) params.set('upstream_svcgen_job_id', opts.upstreamSvcgenJobId)
  if (opts.upstreamJvalJobId) params.set('upstream_jval_job_id', opts.upstreamJvalJobId)
  const res = await fetch(`${base}/editor/files?${params}`, { headers: _editorHeaders() })
  if (!res.ok) throw new Error(`GET /editor/files: ${res.status} ${await res.text()}`)
  return res.json()
}

/**
 * Read a single Java file (overlay-first).
 * @param {string} gateJobId   — pre_flight or java_packaging job ID
 * @param {string} path        — relative file path (e.g. 'PayrollService.java')
 * @param {string} scope       — 'focused' | 'full'
 * @returns {Promise<{content, source, uri}>}
 */
export async function editorReadFile(gateJobId, path, scope = 'focused') {
  const base = _editorBase()
  const params = new URLSearchParams({ job_id: gateJobId, path, editor_scope: scope, gate_job_id: gateJobId })
  const res = await fetch(`${base}/editor/java/file?${params}`, { headers: _editorHeaders() })
  if (!res.ok) throw new Error(`GET /editor/java/file: ${res.status} ${await res.text()}`)
  return res.json()
}

/**
 * Save (overwrite) an existing file into the user overlay.
 * This wraps PUT /editor/file using only the gate job ID.
 * Callers must also supply the full job context (ingest, ca) for the SaveFileRequest contract.
 * Use editorCreateFile() for brand-new files.
 */
export async function editorSaveFile(saveFileRequest) {
  const base = _editorBase()
  const res = await fetch(`${base}/editor/file`, {
    method: 'PUT',
    headers: _editorHeaders(),
    body: JSON.stringify(saveFileRequest),
  })
  if (!res.ok) throw new Error(`PUT /editor/file: ${res.status} ${await res.text()}`)
  return res.json()
}

/**
 * Create a brand-new file that does not exist in base sources.
 * @param {string} gateJobId  — pre_flight or java_packaging job ID
 * @param {string} path       — file path (e.g. 'src/main/java/com/example/StubService.java')
 * @param {string} content    — Java source code
 * @param {string} scope      — 'focused' | 'full'
 * @returns {Promise<{status, path, uri}>}
 */
export async function editorCreateFile(gateJobId, path, content, scope = 'focused') {
  const base = _editorBase()
  const res = await fetch(`${base}/editor/file`, {
    method: 'POST',
    headers: _editorHeaders(),
    body: JSON.stringify({ gate_job_id: gateJobId, path, content, scope }),
  })
  if (!res.ok) throw new Error(`POST /editor/file: ${res.status} ${await res.text()}`)
  return res.json()
}

/**
 * Tombstone-delete a file so it no longer appears in the tree.
 * @param {string} gateJobId  — pre_flight or java_packaging job ID
 * @param {string} path       — file path to tombstone
 * @returns {Promise<{status, path}>}
 */
export async function editorDeleteFile(gateJobId, path) {
  const base = _editorBase()
  const params = new URLSearchParams({ gate_job_id: gateJobId, path })
  const res = await fetch(`${base}/editor/file?${params}`, {
    method: 'DELETE',
    headers: _editorHeaders(),
  })
  if (!res.ok) throw new Error(`DELETE /editor/file: ${res.status} ${await res.text()}`)
  return res.json()
}

/**
 * Reset workspace back to baseline (discard all edits for a pre_flight session).
 * Calls POST /editor/workspace/reset.
 *
 * @param {string} preFlightJobId  — pre_flight job ID
 * @param {string} scoutAccountId  — account ID
 * @param {string} userId          — user ID
 * @returns {Promise<{success: boolean, files_restored: number}>}
 */
export async function editorResetWorkspace(preFlightJobId, scoutAccountId, userId) {
  const base = _editorBase()
  const res = await fetch(`${base}/editor/workspace/reset`, {
    method: 'POST',
    headers: _editorHeaders(),
    body: JSON.stringify({
      pre_flight_job_id: preFlightJobId,
      scout_account_id: scoutAccountId,
      user_id: userId,
    }),
  })
  if (!res.ok) throw new Error(`POST /editor/workspace/reset: ${res.status} ${await res.text()}`)
  return res.json()
}
